/**
 * Upload lab report from the lab desk (PROD-3204, PM slice 4, board N2): one report file, the requests it covers, and
 * whether Scrips reads its values. With reading on, the file goes to Document Intake with the requests as its context;
 * the dialog closes and the rows show the reading (N3). With reading off, the file is attached as today (the
 * Documents upload, then the requests move to Result received). Behind `?x=intake-desk` and Clinic services
 * Document Intake.
 *
 * Pieces: the app `Dialog` at `--dialog-width-large` (demo, 23 Oct: the form width was too small), a dashed drop area, the app `Checkbox`, `Switch` and `Button`
 * (Choose file outline; Cancel outline; the action primary and last).
 */
import { useRef, useState, type DragEvent } from 'react';
import { FileText, Upload, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { BridgeStatusChip } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';
import { formatMrnLabel } from '@/shared/lib/mrn';
import { IntakeApiError } from '@/intake/api/intake-client';
import { useUploadDeskReport } from '@/intake/hooks/use-desk-intake';
import { coverableRequests, fileSizeText } from '@/intake/lib/desk-intake-view';
import { INTAKE_ACCEPT_ATTR, refusalFor, refusalMessage, type IntakeFileRefusal } from '@/intake/lib/intake-file-check';
import type { PatientLabOrderDetail } from '@/types/scheduling-api';

export interface LabReportUploadDialogProps {
  /** The rows on the page: the requests the report can cover come from here. */
  rows: PatientLabOrderDetail[];
  /** The rows the upload starts from (ticked, or one row); null closes the dialog. */
  from: PatientLabOrderDetail[] | null;
  onClose: () => void;
  /**
   * The file was stored: `reading` when reading started, `not-started` when it could not start (the row then says it
   * could not be read, with Read again). The dialog closes and the rows show it.
   */
  onUploaded: (outcome: 'reading' | 'not-started') => void;
  /** Reading off: attach this file to these requests as today. */
  onAttachOnly: (file: File, requests: PatientLabOrderDetail[]) => void;
}

function uploadErrorText(e: unknown): string {
  if (e instanceof IntakeApiError && e.code === 'intake_disabled') return 'Reading reports is not switched on for this clinic.';
  return 'The report could not be uploaded. Check the connection and try again.';
}

export function LabReportUploadDialog({ rows, from, onClose, onUploaded, onAttachOnly }: LabReportUploadDialogProps) {
  // A fresh form each time the dialog opens: the parent mounts this body only while `from` is set.
  return (
    <Dialog open={from !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      {from ? <UploadBody rows={rows} from={from} onClose={onClose} onUploaded={onUploaded} onAttachOnly={onAttachOnly} /> : null}
    </Dialog>
  );
}

function UploadBody({
  rows,
  from,
  onClose,
  onUploaded,
  onAttachOnly,
}: Omit<LabReportUploadDialogProps, 'from'> & { from: PatientLabOrderDetail[] }) {
  const upload = useUploadDeskReport();
  const requests = coverableRequests(rows, from);
  const first = from[0];
  const [checked, setChecked] = useState<Set<string>>(() => new Set(from.map((r) => r.id).filter((id): id is string => !!id)));
  const [file, setFile] = useState<File | null>(null);
  const [refusal, setRefusal] = useState<{ code: IntakeFileRefusal | 'duplicate'; size?: number } | null>(null);
  const [read, setRead] = useState(true);
  const [dragging, setDragging] = useState(false);
  // The upload answered without a file status this dialog knows: never close silently.
  const [unknownAnswer, setUnknownAnswer] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const busy = upload.isPending;

  const take = (files: File[]) => {
    const f = files[0];
    if (!f) return;
    upload.reset();
    setUnknownAnswer(false);
    const code = refusalFor(f);
    setRefusal(code ? { code, size: f.size } : null);
    setFile(code ? null : f);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (!busy) take(Array.from(e.dataTransfer.files ?? []));
  };

  const picked = requests.filter((r) => checked.has(r.id));
  const ready = !!file && picked.length > 0 && !busy;

  const toggle = (id: string) =>
    setChecked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = () => {
    if (!file || !ready || !first?.patientId) return;
    if (!read) {
      onAttachOnly(file, picked);
      return;
    }
    upload.mutate(
      { patientId: first.patientId, file, labRequestIds: picked.map((r) => r.id) },
      {
        onSuccess: (res) => {
          const sent = res.files[0];
          if (sent?.status === 'Refused') {
            setRefusal({ code: (sent.refusedCode ?? 'unsupported_type') as IntakeFileRefusal, size: file.size });
            setFile(null);
          } else if (sent?.status === 'Duplicate') {
            setRefusal({ code: 'duplicate' });
            setFile(null);
          } else if (sent?.status === 'Queued') {
            onUploaded('reading');
          } else if (sent?.status === 'Failed') {
            // Stored, but reading could not start. The row then says it could not be read, with Read again.
            onUploaded('not-started');
          } else {
            setUnknownAnswer(true);
          }
        },
      },
    );
  };

  return (
    <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[var(--dialog-width-large)]">
      <DialogHeader>
        <DialogTitle className="[font:var(--type-card-title)]">Upload lab report</DialogTitle>
        <DialogDescription className="text-text-secondary [font:var(--type-body)]">
          {[first?.patientName, formatMrnLabel(first?.mrn), first?.encounterNumber ? `Encounter ${first.encounterNumber}` : null]
            .filter(Boolean)
            .join(' · ')}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        {file ? (
          <div className="flex items-center gap-3 rounded-card border border-[var(--surface-line)] bg-[var(--surface-panel)] p-4">
            <FileText className="size-5 shrink-0 text-text-secondary" aria-hidden />
            <div className="min-w-0 flex-grow">
              <div className="truncate text-text-primary [font:var(--type-body-strong)]">{file.name}</div>
              <div className="text-text-muted [font:var(--type-meta)]">
                {busy && upload.progress !== null ? `Uploading · ${Math.round(upload.progress * 100)}%` : fileSizeText(file.size)}
              </div>
            </div>
            <Button variant="outline" size="icon-sm" aria-label="Remove file" disabled={busy} onClick={() => setFile(null)}>
              <X aria-hidden />
            </Button>
          </div>
        ) : (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              if (!busy) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            data-testid="lab-report-drop"
            className={cn(
              'flex items-center gap-4 rounded-card border border-dashed p-8',
              dragging
                ? 'border-[var(--accent-primary)] bg-[var(--accent-active-bg)]'
                : 'border-[var(--surface-line)] bg-[var(--surface-panel)]',
            )}
          >
            <Upload className="size-6 shrink-0 text-[var(--accent-primary)]" aria-hidden />
            <div className="min-w-0 flex-grow">
              <div className="text-text-primary [font:var(--type-body-strong)]">Drop the report here, or choose a file</div>
              <div className="text-text-muted [font:var(--type-meta)]">
                PDF, JPG or PNG · up to 10 MB · a phone photo works · HEIC is not accepted
              </div>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept={INTAKE_ACCEPT_ATTR}
              className="sr-only"
              aria-label="Choose file"
              tabIndex={-1}
              onChange={(e) => {
                take(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
            <Button variant="outline" onClick={() => inputRef.current?.click()}>
              Choose file
            </Button>
          </div>
        )}

        {refusal ? (
          <div role="alert" className="flex items-center gap-2">
            <BridgeStatusChip semantic={refusal.code === 'duplicate' ? 'warning' : 'error'}>
              {refusal.code === 'duplicate' ? 'Duplicate' : 'Refused'}
            </BridgeStatusChip>
            <span className="text-text-primary [font:var(--type-body)]">
              {refusal.code === 'duplicate'
                ? 'Same file as an earlier upload. It is already being read or was read.'
                : refusalMessage(refusal.code, refusal.size)}
            </span>
          </div>
        ) : null}

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-2 text-text-primary [font:var(--type-label)]">Requests this report covers</legend>
          {requests.map((r) => (
            <div key={r.id} className="flex items-center gap-3">
              <Checkbox checked={checked.has(r.id)} onCheckedChange={() => toggle(r.id)} disabled={busy} aria-label={r.orderName ?? r.id} />
              <span className="min-w-0 flex-grow text-text-primary [font:var(--type-body)]">
                {r.orderName ?? '—'}
                {r.cptCode ? <span className="text-text-muted"> · {r.cptCode}</span> : null}
              </span>
              <span className="text-text-muted [font:var(--type-meta)]">{r.labVendorName ?? ''}</span>
              <BridgeStatusChip semantic="info">{r.orderStatus}</BridgeStatusChip>
            </div>
          ))}
          <span className="text-text-muted [font:var(--type-meta)]">
            Only this patient&apos;s Sample collected requests from the same encounter are listed.
          </span>
        </fieldset>

        <div className="flex items-start gap-3">
          <Switch checked={read} onCheckedChange={setRead} disabled={busy} aria-label="Read the values from the report" />
          <div className="flex flex-col gap-1">
            <span className="text-text-primary [font:var(--type-body-strong)]">Read the values from the report</span>
            <span className="text-text-secondary [font:var(--type-meta)]">
              Scrips reads each value. You check every value before anything is saved. Off: the report is attached only, as
              today.
            </span>
          </div>
        </div>

        {upload.isError || unknownAnswer ? (
          <p role="alert" className="m-0 text-[var(--status-bad)] [font:var(--type-body)]">
            {upload.isError
              ? uploadErrorText(upload.error)
              : 'The upload did not say whether the report was stored. Check the request before you upload it again.'}
          </p>
        ) : null}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!ready}>
          {busy ? 'Uploading…' : read ? 'Upload and read' : 'Attach report'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
